import React, { FocusEvent, useCallback, useState } from 'react';

import {
  useSession,
  Participation,
  useTaskSetting,
  ModeratedTaskSetting,
  VideoCallProvider,
  Meeting,
  useUpdateMeetingInstructions,
  useTask,
} from '@app/api';
import {
  Button,
  InformationBox,
  Input,
  SaveStatus,
  SharableLink,
  TextArea,
  useToast,
} from 'components';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  noLinkContainer: {
    marginTop: theme.marginBase,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.marginBase * 2 + 'px',
    width: '100%',
  },
  googleSyncButtonContainer: {
    width: 'fit-content',
  },
  box: {
    width: '100%',
  },
  connectButton: {
    marginTop: theme.marginBase * 2,
  },
  linkIcon: {
    fontSize: theme.iconSize.regular,
    marginLeft: theme.marginBase,
  },
}));

interface Props {
  participation: Participation;
  meeting: Meeting;
}

export const AppointmentDetailsVideoCallSection = ({
  participation,
  meeting,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const toast = useToast();
  const { data: session } = useSession(participation.sessionId);
  const { data: taskSetting } = useTaskSetting<ModeratedTaskSetting>(
    session?.taskSettingId,
  );
  const { data: task } = useTask(participation.taskId);

  const linkRef = React.useRef<HTMLInputElement>(null);
  const instructionsRef = React.useRef<HTMLTextAreaElement>(null);

  const onSuccess = useCallback(
    (meeting: Meeting) => {
      if (instructionsRef.current) {
        instructionsRef.current.value = meeting.instructions || '';
      }
      if (linkRef.current) {
        linkRef.current.value = meeting.videoCallLink || '';
      }
    },
    [linkRef, instructionsRef],
  );

  const [videoCallLinksDirty, setVideoCallLinksDirty] = useState(false);
  const onError = useCallback((e: Error) => toast.exception(e), [toast]);
  const {
    isLoading: videoCallLinkSaving,
    error: videoCallLinksError,
    mutate: mutateVideoCall,
  } = useUpdateMeetingInstructions(participation.taskId, {
    onError,
    onSuccess,
  });

  const onVideoCallLinksChange = async (e: FocusEvent<HTMLTextAreaElement>) => {
    setVideoCallLinksDirty(false);
    mutateVideoCall({
      videoCallLink: e.target.value || null,
    });
  };

  const [instructionsDirty, setInstructionsDirty] = useState(false);
  const {
    isLoading: instructionSaving,
    error: instructionsError,
    mutate: mutateInstructions,
  } = useUpdateMeetingInstructions(participation.taskId, {
    onError,
    onSuccess,
  });

  const onInstructionsChange = async (e: FocusEvent<HTMLTextAreaElement>) => {
    setInstructionsDirty(false);
    mutateInstructions({
      instructions: e.target.value || null,
    });
  };

  const isGoogleMeet =
    taskSetting?.videoCallProvider === VideoCallProvider.GOOGLE_MEET;

  const videoCallLink = task?.videoCallLink || meeting.videoCallLink;

  if (!videoCallLink && isGoogleMeet) {
    return (
      <div className={classes.noLinkContainer}>
        <InformationBox
          text={t(
            'sessions.details.monitoring.videoCallLink.calendarMeetingNotCreatedAndNotConnected',
          )}
          variant="danger"
          className={classes.box}
          noIcon
        />
      </div>
    );
  }

  return (
    <>
      {taskSetting?.videoCallProvider !== VideoCallProvider.CUSTOM ? (
        <SharableLink
          label={t('sessions.details.monitoring.videoCallLink.label')}
          secondaryLabel={t(
            'sessions.details.monitoring.videoCallLink.description',
          )}
          link={videoCallLink}
        />
      ) : (
        <Input
          defaultValue={videoCallLink}
          label={t('sessions.details.monitoring.videoCallLink.label')}
          secondaryLabel={t(
            'sessions.details.monitoring.videoCallLink.description',
          )}
          onBlur={onVideoCallLinksChange}
          onChange={() => setVideoCallLinksDirty(true)}
          ref={linkRef}
          rightAction={
            <SaveStatus
              loading={videoCallLinkSaving}
              dirty={videoCallLinksDirty}
              rtl
              error={videoCallLinksError}
              name="videoCallLink"
            />
          }
        />
      )}
      {videoCallLink !== meeting.videoCallLink && (
        <Button
          href={meeting.videoCallLink}
          target="_blank"
          rel="noopener noreferrer"
          small
          className={classes.connectButton}
        >
          {t('sessions.details.monitoring.videoCallLink.join')}
          <i
            className={classNames('ri-external-link-line', classes.linkIcon)}
          />
        </Button>
      )}
      <TextArea
        defaultValue={meeting.instructions}
        label={t('sessions.details.monitoring.instructions.label')}
        secondaryLabel={t(
          'sessions.details.monitoring.instructions.description',
        )}
        onBlur={onInstructionsChange}
        disabled={!!participation.reminderSentAt}
        onChange={() => setInstructionsDirty(true)}
        ref={instructionsRef}
        rightAction={
          <SaveStatus
            key="instructionSaving"
            loading={instructionSaving}
            dirty={instructionsDirty}
            rtl
            error={instructionsError}
            name="instructions"
          />
        }
      />
    </>
  );
};
